import { useState } from 'react';
import { HashRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Heading, Flex } from '@radix-ui/themes';

import SubNav from '../../shared/SubNav';
import { useURLParam } from '../../lib/hooks/useURLParam';

import DownloadReports from './downloadReports';
import CampaignReports from './campaigns/index.jsx';
import AiConversations from './aiConversations/index.jsx';
import AllTiles from './allTiles';
import TileDigestsReports from './tileDigests';
import BroadcastReports from './broadcasts';
import { CampaignFilter } from './components/CampaignFilter.jsx';
import { TileTypeFilter } from './components/TileTypeFilter.jsx';
import { TILE_TYPES } from './constants';

import { useCampaignFilter } from './hooks/useCampaignFilter';

import styles from './Reports.module.css';

const TABS = {
  SUMMARY: 'summary',
  AI_CONVERSATIONS: 'ai-conversations',
  TILES: 'tiles',
  TILE_DIGEST: 'tile-digest',
  BROADCASTS: 'broadcasts',
};

function ReportsContent() {
  const { t } = useTranslation('reports');
  const { campaignId, setCampaignId } = useCampaignFilter();
  const newAirbo = useSelector(state => state.currentBoard.newAirbo);
  const hasAiAssistant = useSelector(
    state => state.currentBoard.hasAiAssistant,
  );
  const [tileType, setTileType] = useState(TILE_TYPES.ALL);

  const navItems = [
    { id: TABS.SUMMARY, label: t('Summary') },
    ...(hasAiAssistant
      ? [{ id: TABS.AI_CONVERSATIONS, label: t('AI Conversations') }]
      : []),
    {
      id: TABS.TILE_DIGEST,
      label: newAirbo ? t('Highlights') : t('Notifications'),
    },
    { id: TABS.BROADCASTS, label: t('Broadcast') },
    { id: TABS.TILES, label: t('Tiles') },
  ];

  const allowedTabs = navItems.map(item => item.id);

  const [getTabFromURL, setTabInURL] = useURLParam('tab', allowedTabs);
  const activeTab = getTabFromURL() || TABS.SUMMARY;

  const handleTabChange = newTab => {
    setTabInURL(newTab);
  };

  return (
    <Flex direction="column" maxWidth="1400px" m="5" gap="5">
      <Flex height="42px" justify="between" align="center">
        <Heading size="6" m="0">
          {t('Reports')}
        </Heading>
        <DownloadReports />
      </Flex>
      <Flex>
        <Flex className={styles.subNavWrapper}>
          <SubNav
            navItems={navItems}
            currentValue={activeTab}
            onValueChange={handleTabChange}
          />
        </Flex>
        <Flex direction="column" gap="4" className={styles.contentContainer}>
          {activeTab !== TABS.AI_CONVERSATIONS &&
            activeTab !== TABS.BROADCASTS && (
              <Flex direction="row" gap="3" align="center">
                <CampaignFilter
                  campaignId={campaignId}
                  setCampaignId={setCampaignId}
                />
                {activeTab === TABS.TILES && (
                  <TileTypeFilter
                    tileType={tileType}
                    setTileType={setTileType}
                  />
                )}
              </Flex>
            )}
          {activeTab === TABS.SUMMARY && <CampaignReports />}
          {activeTab === TABS.AI_CONVERSATIONS && <AiConversations />}
          {activeTab === TABS.TILES && <AllTiles tileType={tileType} />}
          {activeTab === TABS.TILE_DIGEST && <TileDigestsReports />}
          {activeTab === TABS.BROADCASTS && <BroadcastReports />}
        </Flex>
      </Flex>
    </Flex>
  );
}

const ReportsContainer = () => {
  return (
    <HashRouter hashType="noslash">
      <ReportsContent />
    </HashRouter>
  );
};

export default ReportsContainer;
